import { defineComponent as _defineComponent } from 'vue'
import { withDefaults, ref, toRefs, computed, watch, onMounted } from 'vue';
import type { ContentReference } from '@ilteducation/content-collections-types';
import { debounce } from 'lodash';
import { term } from 'lucene';
import store from '../store';
import useCollectionUtils, { type ContentType } from "@/resources/collections/use-collection-utils";

type Emits = {
  (e: 'add', reference: { data } & ContentReference): void;
};
type Props = {
  realm?: string;
  context?: string;
  contentTypes?: ContentType[];
};

export default /*#__PURE__*/_defineComponent({
  __name: 'ContentAutoComplete',
  props: {
    realm: null,
    context: { default: undefined },
    contentTypes: null
  },
  emits: ["add"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'add', reference: { data } & ContentReference): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { realm?: string, context: string, contentTypes?: ContentType[] }




const { realm, context } = toRefs(props);

const { getTitleInRealmLanguage, contentTypes: defaultContentTypes } = useCollectionUtils();
const contentTypes = props.contentTypes || defaultContentTypes;

const selectedContentType = ref<{ name: string; route: string } | undefined>(undefined);
const selectedContent = ref<Entity>(undefined);
const entries = ref([]);
const isLoading = ref<boolean>(false);
const search = ref(null);

const items = computed(() =>
  entries.value?.map((entry) => {
    let title = '';
    let subtitle = '';
    if (selectedContentType.value.name === 'Video') {
      title = getTitleInRealmLanguage(realm.value, entry.title);
      subtitle = `FilmId: ${entry.filmId}`;
      if (entry.filmId) {
        subtitle = `FilmId: ${entry.filmId}`;
      }
    } else if (selectedContentType.value.name === 'Article') {
      title = entry.headline;
      subtitle = entry?.publisher?.name || '';
    } else if (selectedContentType.value.name === 'Material') {
      title = `${entry.title} (${entry.language})`
      subtitle = `ID: ${entry.id}`;
      }
    else {
      title = entry.title;
      subtitle = entry?.publisher?.name || '';
    }
    return { title, id: entry.id, subtitle, data: entry };
  }),
);

// Debounced rather than guarded on isLoading: the old guard silently discarded every
// keystroke typed while a request was in flight, so fast typing left stale results behind.
const runSearch = debounce(async (value: string) => {
  isLoading.value = true;

  const filter = {
    q: term.escape(value),
    ...(selectedContentType.value?.name !== 'Video' &&
      selectedContentType.value?.name !== 'Article' && {
        // availabilityRealms, not realm - `realm` is not queryable on the content-search indexes
        availabilityRealms: realm.value,
        ...(context.value && { 'accessRules.subscriptionType': context.value }),
      }),
  };
  /**
   * Load paginated and sorted data list
   */
  try {
    const response = await store.dispatch(`${selectedContentType.value.route}/getList`, {
      pagination: {
        page: 1,
        perPage: 50,
      },
      filter,
    });
    // Assigned unconditionally so an empty result clears the list instead of leaving
    // the previous search's hits on screen.
    entries.value = response?.data ?? [];
  } catch (err) {
    console.error(err);
    entries.value = [];
  } finally {
    isLoading.value = false;
  }
}, 300);

const reset = () => {
  runSearch.cancel();
  entries.value = [];
  search.value = null;
  selectedContent.value = undefined;
};

// Cancel any in-flight search on a type/realm/context switch, otherwise a late response
// repopulates entries that the `items` computed then labels with the wrong type's mapping.
watch(selectedContentType, reset);
watch([context, realm], reset);

watch(search, (value, oldValue) => {
  if (!value || value.length < 3 || value === oldValue) return;
  runSearch(value);
});

const onAdd = () => {
  emits('add', {
    id: selectedContent.value.id,
    type: selectedContentType.value.name,
    data: selectedContent.value.data,
  });
};

onMounted(() => {
  // eslint-disable-next-line prefer-destructuring
  selectedContentType.value = contentTypes[0];
});

return { __sfc: true,emits, props, realm, context, getTitleInRealmLanguage, defaultContentTypes, contentTypes, selectedContentType, selectedContent, entries, isLoading, search, items, runSearch, reset, onAdd }
}

})